const HIDDEN_GRACE_MS = 60_000;
const REFRESH_DEBOUNCE_MS = 250;
const MAX_BACKOFF_MS = 30_000;
const SESSION_EXPIRED = 1008;

export default defineNuxtPlugin(() => {
  const config = useRuntimeConfig();
  const tokens = useTokens();
  const user = useUser();

  let socket = null;
  let attempts = 0;
  let retryTimer = null;
  let hiddenTimer = null;
  let refreshTimer = null;
  let waitingForToken = false;

  const liveUrl = () => `${config.public.apiUrl.replace(/^http/, 'ws').replace(/\/$/, '')}/api/v3/live`;

  const refreshParker = () => {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      if (user.getParkerId) useParker(user.getParkerId);
    }, REFRESH_DEBOUNCE_MS);
  };

  const handle = (raw) => {
    let message;
    try {
      message = JSON.parse(raw);
    } catch {
      return;
    }
    if (message.type !== 'event') return;
    refreshParker();
    window.dispatchEvent(new CustomEvent('spotsurfer:live', { detail: message }));
  };

  const stop = () => {
    clearTimeout(retryTimer);
    if (socket) {
      socket.onclose = null;
      socket.close(1000);
      socket = null;
    }
  };

  const scheduleRetry = () => {
    clearTimeout(retryTimer);
    const wait = Math.min(1000 * 2 ** attempts, MAX_BACKOFF_MS);
    attempts += 1;
    retryTimer = setTimeout(connect, wait + Math.random() * 500);
  };

  function connect() {
    stop();
    if (!tokens.access || !user.getParkerId || document.hidden) return;
    waitingForToken = false;
    const ws = new WebSocket(liveUrl());
    socket = ws;
    ws.onopen = () => ws.send(JSON.stringify({ type: 'auth', token: tokens.access }));
    ws.onmessage = (e) => {
      if (typeof e.data !== 'string') return;
      if (e.data.includes('"ready"')) {
        attempts = 0;
        refreshParker();
        return;
      }
      handle(e.data);
    };
    ws.onclose = (e) => {
      if (socket !== ws) return;
      socket = null;
      if (e.code === SESSION_EXPIRED) {
        waitingForToken = true;
        return;
      }
      scheduleRetry();
    };
  }

  watch(() => tokens.access, (access) => {
    attempts = 0;
    if (access) connect();
    else stop();
  });
  watch(() => user.getParkerId, () => connect());

  document.addEventListener('visibilitychange', () => {
    clearTimeout(hiddenTimer);
    if (document.hidden) {
      hiddenTimer = setTimeout(stop, HIDDEN_GRACE_MS);
    } else if (!socket && !waitingForToken) {
      attempts = 0;
      connect();
    }
  });

  window.addEventListener('online', () => {
    if (!socket && !waitingForToken) connect();
  });

  connect();
});
